import { useState } from 'react';
import { useWindowManager, WindowType } from '@/contexts/WindowManagerContext';
import { X, FileText, User, Building, Package, Upload, BookOpen, RotateCcw, Tag, Users, Loader2, ChevronUp, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const windowIcons: Record<WindowType, React.ElementType> = {
  'invoice': FileText,
  'customer': User,
  'vendor': Building,
  'product': Package,
  'bulk-upload-products': Upload,
  'bulk-upload-customers': Upload,
  'bulk-upload-vendors': Upload,
  'ledger-entry': BookOpen,
  'return-invoice': RotateCcw,
  'category': Tag,
  'customer-group': Users,
  'vendor-group': Users,
};

export const WindowTaskbar = () => {
  const { windows, restoreWindow, closeWindow, bringToFront } = useWindowManager();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const minimizedWindows = windows.filter(w => w.isMinimized);
  const hasMinimizedWindows = minimizedWindows.length > 0;

  // When no windows minimized, show slim bar; otherwise show full taskbar
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-fade-in"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Main taskbar */}
      <div className={cn(
        "bg-background/95 backdrop-blur-md border-t border-border shadow-lg",
        "flex items-center",
        "transition-[height,padding,background-color,border-color,box-shadow] duration-300 ease-out",
        hasMinimizedWindows 
          ? "h-12 px-4 gap-3 animate-[taskbar-expand_0.3s_ease-out]" 
          : "h-2 justify-center cursor-pointer hover:h-3 hover:bg-muted/50",
        hasMinimizedWindows && isExpanded && "bg-background border-primary/40 shadow-primary/10"
      )}>
        {/* Slim bar indicator when no windows */}
        {!hasMinimizedWindows && (
          <ChevronUp className="h-2 w-2 text-muted-foreground/50" />
        )}

        {/* Minimized windows indicator - only show when there are windows */}
        {hasMinimizedWindows && (
          <div className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors",
            "bg-primary/10 border-primary/20"
          )}>
            <div className="relative">
              <ChevronUp className={cn(
                "h-4 w-4 text-primary transition-transform duration-300",
                isExpanded && "rotate-180"
              )} />
            </div>
            <span className="text-sm font-medium text-primary">
              {minimizedWindows.length}
            </span>
            <span className="text-sm text-muted-foreground hidden sm:inline">
              window{minimizedWindows.length > 1 ? 's' : ''} minimized
            </span>
          </div>
        )}

        {/* Window tabs - only show when there are minimized windows */}
        {hasMinimizedWindows && (
          <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {minimizedWindows.map((window) => {
              const Icon = windowIcons[window.type] || FileText;
              const hasProgress = window.progress?.isLoading;
              const progressPercent = window.progress?.progressPercent;
              
              return (
                <div
                  key={window.id}
                  role="button"
                  tabIndex={0}
                  className={cn(
                    "group flex items-center gap-2 px-3 py-2 rounded-lg",
                    "bg-muted/40 hover:bg-muted border border-border/50 hover:border-primary/50",
                    "cursor-pointer transition-all duration-200 min-w-0 shrink-0",
                    hasProgress && "border-primary/40 bg-primary/5"
                  )}
                  onClick={() => {
                    restoreWindow(window.id);
                    bringToFront(window.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      restoreWindow(window.id);
                      bringToFront(window.id);
                    }
                  }}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-md flex items-center justify-center shrink-0",
                    hasProgress ? "bg-primary/20" : "bg-primary/10"
                  )}>
                    {hasProgress ? (
                      <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    ) : (
                      <Icon className="h-4 w-4 text-primary" />
                    )}
                  </div>
                  
                  <span className="text-sm text-foreground truncate max-w-[120px]">
                    {window.title}
                  </span>

                  {/* Progress indicator */}
                  {hasProgress && progressPercent !== undefined && (
                    <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  )}
                  
                  {/* Close button */}
                  <button
                    type="button"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/20 hover:text-destructive shrink-0 rounded-md flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeWindow(window.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Expanded panel - only show if there are minimized windows */}
      {hasMinimizedWindows && (
        <div className={cn(
          "absolute bottom-12 left-0 right-0",
          "bg-background/98 backdrop-blur-lg border-t border-x border-primary/30 rounded-t-xl shadow-2xl",
          "transition-all duration-300 ease-out overflow-hidden",
          isExpanded ? "max-h-[300px] opacity-100 translate-y-0" : "max-h-0 opacity-0 translate-y-4 pointer-events-none"
        )}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Minimized Windows
              </h3>
              <span className="text-xs text-muted-foreground">
                Click to restore
              </span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {minimizedWindows.map((window) => {
                const Icon = windowIcons[window.type] || FileText;
                const hasProgress = window.progress?.isLoading;
                const progressPercent = window.progress?.progressPercent;
                const progressLabel = window.progress?.progressLabel;
                
                return (
                  <div
                    key={window.id}
                    className={cn(
                      "group relative flex flex-col p-3 rounded-lg",
                      "bg-muted/30 hover:bg-muted/60 border border-border/50 hover:border-primary/50",
                      "cursor-pointer transition-all duration-200",
                      "hover:shadow-lg hover:shadow-primary/5",
                      hasProgress && "border-primary/30 bg-primary/5"
                    )}
                    onClick={() => {
                      restoreWindow(window.id);
                      bringToFront(window.id);
                    }}
                  >
                    {/* Window header */}
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        hasProgress ? "bg-primary/20" : "bg-primary/10",
                        "transition-colors"
                      )}>
                        {hasProgress ? (
                          <Loader2 className="h-5 w-5 text-primary animate-spin" />
                        ) : (
                          <Icon className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {window.title}
                        </p>
                        {progressLabel && (
                          <p className="text-xs text-muted-foreground truncate">
                            {progressLabel}
                          </p>
                        )}
                      </div>
                      
                      {/* Close button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/20 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeWindow(window.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Progress bar */}
                    {hasProgress && (
                      <div className="mt-3 space-y-1">
                        {progressPercent !== undefined ? (
                          <div className="space-y-1">
                            <Progress 
                              value={progressPercent} 
                              className="h-2 bg-muted" 
                            />
                            <p className="text-xs text-right text-muted-foreground">
                              {progressPercent}%
                            </p>
                          </div>
                        ) : (
                          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full w-1/3 bg-primary rounded-full"
                              style={{ 
                                animation: 'shimmer 1.5s ease-in-out infinite',
                                background: 'linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.5) 50%, hsl(var(--primary)) 100%)',
                                backgroundSize: '200% 100%'
                              }} 
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
